import React from 'react';
import { Button, Card, Form, Alert } from 'react-bootstrap';
import { FaBold, FaItalic, FaUnderline, FaLink, FaListUl, FaListOl, FaAlignLeft, FaPlus, FaImage } from 'react-icons/fa';

const EventPolicies = ({
  policies,
  setPolicies,
  error,
  setError,
  editorRefs,
  handleTitleChange,
  handleTypeChange,
  handleContentChange,
  addPolicy,
  deletePolicy,
  handleSaveEvent,
  validateImage,
  uploadFilesAWS,
  showToast
}) => {
  const applyFormatting = (id, formatType) => {
    if (editorRefs.current[id]) {
      editorRefs.current[id].focus();
    }

    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'underline':
        document.execCommand('underline', false, null);
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (url) {
          document.execCommand('createLink', false, url);
        }
        break;
      case 'ul':
        document.execCommand('insertUnorderedList', false, null);
        break;
      case 'ol':
        document.execCommand('insertOrderedList', false, null);
        break;
      case 'align-left':
        document.execCommand('justifyLeft', false, null);
        break;
      default:
        return;
    }

    if (editorRefs.current[id]) {
      handleContentChange(id, editorRefs.current[id].innerHTML);
    }
  };

  const handleEditorInput = (id) => {
    if (editorRefs.current[id]) {
      handleContentChange(id, editorRefs.current[id].innerHTML);
    }
  };

  const handleFileChange = (id) => async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Handle document uploads
    const allowedDocTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (allowedDocTypes.includes(file.type)) {
      // Validate document size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('Document size must not exceed 5MB.');
        setTimeout(() => setError(''), 3000);
        return;
      }

      // Upload document to AWS
      try {
        const res = await uploadFilesAWS({ upload_files: file });
        if (res.data && res.data.success === true) {
          const uploadedUrl = res.data.uploadedUrls.upload_files[0];
          setPolicies(policies.map(policy =>
            policy.id === id ? { ...policy, file, content: file.name, imageUrl: uploadedUrl } : policy
          ));
          showToast('Document uploaded successfully', 'success');
          // await handleSaveEvent(uploadedUrl, 'document');
        } else {
          setError(res.message);
          setTimeout(() => setError(''), 3000);
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        setError('Failed to upload document. Please try again.');
        setTimeout(() => setError(''), 3000);
      }
      return;
    }

    // Handle image uploads
    const validationError = await validateImage(file, 'policy');
    if (validationError) {
      setError(validationError);
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Upload image to AWS
    try {
      const res = await uploadFilesAWS({ upload_files: file });
      if (res.data && res.data.success === true) {
        const uploadedUrl = res.data.uploadedUrls.upload_files[0];
        setPolicies(policies.map(policy =>
          policy.id === id ? { ...policy, imageUrl: uploadedUrl, content: file.name, file: null } : policy
        ));
        showToast('Image uploaded successfully', 'success');
        // await handleSaveEvent(uploadedUrl, 'image');
      } else {
        setError(res.message);
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error uploading policy image:', error);
      setError('Failed to upload policy image. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const removeImage = (id) => () => {
    setPolicies(policies.map(policy =>
      policy.id === id ? { ...policy, imageUrl: null, content: policy.type === 'document' ? policy.content : '', file: null } : policy
    ));
  };

  return (
    <div className='card'>
      {/* <div className="card-header setting_card_heading setting_card_heading_size">
        <h5>Event Policies</h5>
        <p>Add up to 3 policies.</p>
      </div> */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      <div className="">
        <div className="card-body border-0 p-0">
          {policies.map(policy => (
            <div key={policy.id} className="mb-4 p-4 border rounded mt-3">
              <div className="mb-3">
                <label className="form-label">
                  Policy Title <span style={{ color: 'red' }}>*</span>
                </label>
                <div className="d-flex gap-3 align-center">
                  <input
                    type="text"
                    className="form-control"
                    value={policy.title}
                    onChange={(e) => handleTitleChange(policy.id, e.target.value)}
                  />
                  <span className=" " >
                    {policy.characterCount}/{policy.maxCharacters}
                  </span>
                </div>
              </div>

              <div className="mb-3 input_pollices_flex">
                {['Text', 'Url', 'Document', 'Image'].map(type => (
                  <div className="form-check" key={type}>
                    <input
                      type="radio"
                      id={`${type}-${policy.id}`}
                      name={`policy-type-${policy.id}`}
                      checked={policy.type === type.toLowerCase()}
                      onChange={() => handleTypeChange(policy.id, type.toLowerCase())}
                      className="form-check-input"
                    />
                    <label
                      htmlFor={`${type}-${policy.id}`}
                      className="form-check-label"
                    >
                      {type}
                    </label>
                  </div>
                ))}
              </div>

              {policy.type === 'url' && (
                <input
                  type="text"
                  placeholder="Enter URL"
                  className="form-control mb-3"
                  value={policy.content}
                  onChange={(e) => handleContentChange(policy.id, e.target.value)}
                />
              )}

              {policy.type === 'text' && (
                <>
                  <div className="border d-flex p-2 mb-0 rounded-top bor_clr">
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'bold')}>
                      <FaBold />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'italic')}>
                      <FaItalic />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'underline')}>
                      <FaUnderline />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'link')}>
                      <FaLink />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'ul')}>
                      <FaListUl />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'ol')}>
                      <FaListOl />
                    </button>
                    <button type="button" className="btn btn-light btn-sm me-1 border-0" onClick={() => applyFormatting(policy.id, 'align-left')}>
                      <FaAlignLeft />
                    </button>
                  </div>

                  <div
                    ref={el => editorRefs.current[policy.id] = el}
                    className="form-control border-top-0"
                    style={{
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      minHeight: '200px',
                      height: 'auto',
                      overflowY: 'auto',
                      padding: '0.375rem 0.75rem'
                    }}
                    contentEditable="true"
                    dangerouslySetInnerHTML={{ __html: policy.content }}
                    onInput={() => handleEditorInput(policy.id)}
                  />
                </>
              )}

              {policy.type === 'document' && (
                <div>
                  <input
                    type="file"
                    className="form-control mb-3"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange(policy.id)}
                  />
                  {policy.content && (
                    <div className="mb-3">
                      <small>Selected file: {policy.content}</small>
                    </div>
                  )}
                </div>
              )}

              {policy.type === 'image' && (
                <div>
                  <input
                    type="file"
                    className="form-control mb-3"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleFileChange(policy.id)}
                  />
                  {policy.imageUrl && (
                    <div className="mb-3">
                      <img src={policy.imageUrl} alt="Policy" style={{ maxWidth: '150px', maxHeight: '100px' }} />
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm ms-2"
                        onClick={removeImage(policy.id)}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                  {policy.content && (
                    <div className="mb-3">
                      <small>Selected file: {policy.content}</small>
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-end mt-3 gap-3">
                <button type="button" className="btn btn-outline-danger" onClick={() => deletePolicy(policy.id)}>Delete</button>
                <button type="button" className="btn btn-outline-primary" disabled={!policy.title} onClick={handleSaveEvent}>Save</button>
              </div>
            </div>
          ))}

          {policies.length < 3 && (
            <button type="button" className="btn btn-outline-primary  gap-1" onClick={addPolicy}>
              <FaPlus className="font-13" /> Add Policy
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventPolicies;